/**
 * Outil « Sommes partielles » (Boîte à outils, prépa).
 *
 * Quatre familles de séries dont la nature est connue (Riemann, alternée,
 * géométrique, exponentielle), un curseur pour leur paramètre, le tracé des
 * sommes partielles Sₙ (échelle log pour voir Sₙ ≈ ln n + γ), la somme S
 * quand elle existe, le reste et son majorant du cours, le rang à partir
 * duquel |S − Sₙ| < ε (ou Sₙ > A), le programme Python. Le verdict vient du
 * cours : 5 000 termes de Σ 1/n ne montrent PAS la divergence, l'outil le
 * dit. Défi : « converge ou diverge ? » sur 5 séries. Calculs : `series.ts`.
 */

import { useMemo, useState } from 'react';
import { MathText, cn, formatTick, niceStep, ticks } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { makeFrame, type Frame } from '../../ui/frame';
import { formatNumber, formatSci, numberAtoms, superscript } from '../../ui/format';
import { AXIS, FIGURE_BG_CLASS, GRID, HaloText } from '../../ui/svg-parts';
import {
  DEFAULT_PARAMS,
  divergenceRank,
  firstIndex,
  formatCount,
  N_CHOICES,
  partialSums,
  pickQuiz,
  precisionRank,
  pythonProgram,
  QUIZ_ROUNDS,
  RANGES,
  remainder,
  remainderBound,
  seriesTex,
  verdict,
  yRange,
  type QuizItem,
  type SeriesKind,
  type SeriesParams,
} from './series';

const SIZE = 320;
const POINT = 'var(--color-primary-500)';
const EVEN = 'var(--color-accent-500)';
const LIMIT = 'var(--color-xp-500)';
const GHOST = 'var(--color-ink-300)';
const GROWTH = 'var(--color-ink-500)';

const KIND_OPTIONS: ReadonlyArray<{ value: SeriesKind; label: string }> = [
  { value: 'riemann', label: 'Riemann' },
  { value: 'alternating', label: 'Alternée' },
  { value: 'geometric', label: 'Géométrique' },
  { value: 'exponential', label: 'Exponentielle' },
];

const EPS_OPTIONS = [
  { value: '0.1', label: '0,1' },
  { value: '0.01', label: '0,01' },
  { value: '0.001', label: '0,001' },
  { value: '0.000001', label: '10⁻⁶' },
] as const;
type EpsValue = (typeof EPS_OPTIONS)[number]['value'];

const A_OPTIONS = [
  { value: '5', label: '5' },
  { value: '10', label: '10' },
  { value: '15', label: '15' },
  { value: '20', label: '20' },
] as const;
type AValue = (typeof A_OPTIONS)[number]['value'];

const SUB = '₀₁₂₃₄₅₆₇₈₉';
const sub = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUB[Number(c)] ?? c)
    .join('');
/** 0,00995 · 2 × 10⁻⁷ */
const small = (v: number) => (v === 0 ? '0' : v >= 0.001 ? formatNumber(v, 5) : formatSci(v, 1));
const fmtS = (v: number) =>
  Math.abs(v) >= 1e6 ? `${v < 0 ? '−' : ''}${formatSci(Math.abs(v), 2)}` : formatNumber(v, 6);

export function PartialSumsTool({
  onSend,
  sendCost,
  rng = Math.random,
}: ToolProps & { rng?: () => number }) {
  const [kind, setKind] = useState<SeriesKind>('riemann');
  const [p, setP] = useState<SeriesParams>(DEFAULT_PARAMS);
  const [nIndex, setNIndex] = useState(3);
  const [logScale, setLogScale] = useState(false);
  const [ghost, setGhost] = useState(false);
  const [eps, setEps] = useState<EpsValue>('0.01');
  const [aValue, setAValue] = useState<AValue>('10');
  const [quiz, setQuiz] = useState<QuizState | null>(null);

  const choices = N_CHOICES[kind];
  const n = choices[Math.min(nIndex, choices.length - 1)]!;
  const first = firstIndex(kind);
  const logOk = kind === 'riemann' || kind === 'alternating';
  const log = logOk && logScale;

  const v = useMemo(() => verdict(kind, p), [kind, p]);
  const sums = useMemo(() => partialSums(kind, p, n), [kind, p, n]);
  const sN = sums[sums.length - 1]!;
  const ghostSums = useMemo(
    () =>
      ghost && kind === 'riemann' && Math.abs(p.alpha - 1) > 1e-9
        ? partialSums('riemann', { ...p, alpha: 1 }, n)
        : null,
    [ghost, kind, p, n],
  );
  const bound = remainderBound(kind, p, n);
  const rest = remainder(kind, p, n);
  const threshold = useMemo(
    () =>
      v.converges
        ? precisionRank(kind, p, Number(eps))
        : v.behaviour === 1
          ? divergenceRank(kind, p, Number(aValue))
          : null,
    [v, kind, p, eps, aValue],
  );

  const frame = useMemo(() => {
    const [lo, hi] = yRange([...sums, ...(ghostSums ?? [])], v.sum?.value ?? null);
    const xMin = log ? -0.08 : first - 0.04 * n - 0.3;
    const xMax = log ? Math.log10(n) * 1.03 + 0.02 : n + 0.04 * n + 0.3;
    return makeFrame({ xMin, xMax, yMin: lo, yMax: hi }, SIZE);
  }, [sums, ghostSums, v, log, first, n]);
  const X = (k: number) => (log ? Math.log10(k) : k);

  const switchKind = (k: SeriesKind) => {
    setKind(k);
    setNIndex((i) => Math.min(i, N_CHOICES[k].length - 1));
  };
  const setParam = (key: keyof SeriesParams) => (val: number) =>
    setP((old) => ({ ...old, [key]: val }));

  const offFrame = sums.findIndex((s) => Math.abs(s) > 1e6);
  // termes de signes alternés : sommes d'indice pair et impair en deux couleurs
  const alternate =
    kind === 'alternating' ||
    (kind === 'geometric' && p.q < 0) ||
    (kind === 'exponential' && p.x < 0);

  return (
    <div className="space-y-4">
      <p className="text-ink-600 text-sm">
        Choisis une série, règle son paramètre et regarde ses sommes partielles Sₙ s’approcher (ou
        non) d’une limite.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Famille de séries"
          value={kind}
          options={KIND_OPTIONS}
          onChange={switchKind}
        />
        <ToggleChip on={quiz !== null} onClick={() => setQuiz(quiz ? null : startQuiz(rng))}>
          Converge ou diverge ?
        </ToggleChip>
      </div>

      {quiz ? (
        <QuizPanel state={quiz} onChange={setQuiz} onRestart={() => setQuiz(startQuiz(rng))} />
      ) : (
        <>
          <section
            className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
            aria-label="Série"
            data-testid="ps-series"
          >
            <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$\\displaystyle ${seriesTex(kind, p)}$`}</MathText>
            {kind === 'riemann' && (
              <ParamSlider
                name="α"
                ariaLabel="Exposant α"
                value={p.alpha}
                {...RANGES.alpha}
                format={(x) => formatNumber(x, 1)}
                onChange={setParam('alpha')}
              />
            )}
            {kind === 'alternating' && (
              <ParamSlider
                name="α"
                ariaLabel="Exposant α"
                value={p.altAlpha}
                {...RANGES.altAlpha}
                format={(x) => formatNumber(x, 1)}
                onChange={setParam('altAlpha')}
              />
            )}
            {kind === 'geometric' && (
              <ParamSlider
                name="q"
                ariaLabel="Raison q"
                value={p.q}
                {...RANGES.q}
                format={(x) => formatNumber(x, 2)}
                onChange={setParam('q')}
              />
            )}
            {kind === 'exponential' && (
              <ParamSlider
                name="x"
                ariaLabel="Réel x"
                value={p.x}
                {...RANGES.x}
                format={(x) => formatNumber(x, 1)}
                onChange={setParam('x')}
              />
            )}
          </section>

          {logOk && (
            <div className="flex flex-wrap gap-2">
              {logOk && (
                <ToggleChip on={logScale} onClick={() => setLogScale((o) => !o)}>
                  Échelle log pour n
                </ToggleChip>
              )}
              {kind === 'riemann' && Math.abs(p.alpha - 1) > 1e-9 && (
                <ToggleChip on={ghost} onClick={() => setGhost((o) => !o)}>
                  Comparer à Σ 1/n
                </ToggleChip>
              )}
            </div>
          )}

          <div className="mx-auto w-full max-w-[26rem]">
            <svg
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className={cn(
                'border-ink-100 block aspect-square w-full select-none rounded-2xl border',
                FIGURE_BG_CLASS,
              )}
              role="img"
              aria-label={`Sommes partielles S${sub(first)} à S${sub(n)}${log ? ', n en échelle logarithmique' : ''}${v.sum ? `, et la somme S ≈ ${fmtS(v.sum.value)}` : ''}.`}
              data-testid="ps-figure"
            >
              <Axes frame={frame} log={log} />
              {v.sum && (
                <g>
                  <line
                    x1={0}
                    y1={frame.ty(v.sum.value)}
                    x2={SIZE}
                    y2={frame.ty(v.sum.value)}
                    stroke={LIMIT}
                    strokeWidth={2}
                    strokeDasharray="7 5"
                  />
                  <HaloText
                    x={SIZE - 8}
                    y={frame.ty(v.sum.value) - 6}
                    textAnchor="end"
                    fill={LIMIT}
                    fontWeight={700}
                  >
                    S
                  </HaloText>
                </g>
              )}
              {v.growth && (
                <path
                  d={curvePath(frame, first, n, (k) => v.growth!(k), X)}
                  fill="none"
                  stroke={GROWTH}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  data-testid="ps-growth"
                />
              )}
              {ghostSums && (
                <path
                  d={sumsPath(frame, ghostSums, first, X)}
                  fill="none"
                  stroke={GHOST}
                  strokeWidth={2}
                  data-testid="ps-ghost"
                />
              )}
              <SumsLayer frame={frame} sums={sums} first={first} X={X} alternate={alternate} />
            </svg>
          </div>
          {(v.growth || ghostSums || (alternate && sums.length <= 60)) && (
            <ul className="text-ink-600 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {alternate && sums.length <= 60 && (
                <>
                  <Legend color={POINT}>S d’indice impair</Legend>
                  <Legend color={EVEN}>S d’indice pair</Legend>
                </>
              )}
              {v.growthTex && (
                <Legend color={GROWTH} dashed>
                  <MathText>{`$${v.growthTex}$`}</MathText>
                </Legend>
              )}
              {ghostSums && <Legend color={GHOST}>Σ 1/n</Legend>}
            </ul>
          )}

          <Stepper
            label="Nombre de termes"
            value={Math.min(nIndex, choices.length - 1)}
            min={0}
            max={choices.length - 1}
            onChange={setNIndex}
            format={(i) => `jusqu’à n = ${formatCount(choices[i]!)}`}
          />
          {offFrame >= 0 && (
            <p className="text-ink-500 text-sm">
              À partir de S{sub(offFrame + first)}, les sommes dépassent 10⁶ en valeur absolue :
              elles sortent du cadre.
            </p>
          )}

          <div className="flex min-h-12 flex-wrap items-center gap-2">
            <p
              className="text-ink-900 min-w-0 flex-1 text-base font-semibold"
              data-testid="ps-reading"
            >
              S{sub(n)} ≈ {fmtS(sN)}
            </p>
            {onSend && Number.isFinite(sN) && Math.abs(sN) < 1e6 && (
              <SendToSheetButton
                label={formatNumber(sN, 4)}
                cost={sendCost}
                onClick={() => onSend({ atoms: numberAtoms(sN, 4) })}
              />
            )}
          </div>

          <section
            aria-label="Nature de la série"
            className={cn(
              'space-y-1 rounded-2xl px-3 py-2',
              v.converges ? 'bg-success-50' : 'bg-error-50',
            )}
            data-testid="ps-verdict"
          >
            <div className="flex min-h-11 items-center gap-2">
              <p className="text-ink-900 min-w-0 flex-1 text-base font-bold">
                {v.converges && v.sum
                  ? 'La série converge.'
                  : v.behaviour === 1
                    ? 'La série diverge : Sₙ tend vers +∞.'
                    : 'La série diverge : Sₙ n’a pas de limite.'}
              </p>
              {onSend && v.sum?.atoms && (
                <SendToSheetButton
                  label={v.sum.plain}
                  cost={sendCost}
                  onClick={() => onSend({ atoms: v.sum!.atoms! })}
                />
              )}
            </div>
            {v.converges && v.sum && (
              <MathText className="text-ink-900 block overflow-x-auto text-lg">{`$S = ${sumTex(v.sum)}$`}</MathText>
            )}
            <p className="text-ink-700 text-sm">{v.reason}</p>
            {v.sum?.approxOnly && (
              <p className="text-ink-500 text-xs">Pas de forme exacte connue : valeur approchée.</p>
            )}
            {v.sum && (
              <p className="text-ink-700 text-sm" data-testid="ps-remainder">
                {bound?.exact
                  ? `Reste : |S − S${sub(n)}| = ${small(bound.value)} (${bound.text}).`
                  : `Reste : |S − S${sub(n)}| ≈ ${small(rest ?? 0)}${bound ? ` ≤ ${small(bound.value)} (${bound.text}).` : '.'}`}
              </p>
            )}
          </section>

          {kind === 'riemann' && p.alpha <= 1 + 1e-9 && p.alpha >= 0.9 - 1e-9 && (
            <Note live={false}>
              Avec {formatCount(n)} termes, S{sub(n)} ≈ {formatNumber(sN, 2)} : à l’œil, on croirait
              une limite. Le calcul ne prouve rien ; c’est le cours qui tranche.
            </Note>
          )}
          {kind === 'riemann' && p.alpha < 0.9 - 1e-9 && (
            <Note live={false}>
              Ici la croissance se voit : Sₙ grandit comme n^(1−α)/(1 − α). Le tracé le suggère, le
              cours le prouve.
            </Note>
          )}
          {kind === 'riemann' && p.alpha > 1 && p.alpha < 1.35 && (
            <Note live={false}>
              α est proche de 1 : la série converge, mais si lentement que le tracé ressemble à
              celui d’une série divergente.
            </Note>
          )}

          {threshold && (
            <section
              className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
              aria-label="Seuil"
              data-testid="ps-threshold"
            >
              {v.converges ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-ink-700 text-sm font-semibold">Précision ε</span>
                    <Segmented
                      ariaLabel="Précision ε"
                      value={eps}
                      options={EPS_OPTIONS}
                      onChange={setEps}
                    />
                  </div>
                  <p className="text-ink-900 text-sm">
                    {threshold.exact
                      ? `|S − Sₙ| < ${labelOf(EPS_OPTIONS, eps)} dès n = ${formatCount(threshold.n)}.`
                      : `|S − Sₙ| < ${labelOf(EPS_OPTIONS, eps)} : il faut n ≈ ${formatCount(threshold.n)} termes (ordre de grandeur, tiré d’un développement du reste).`}
                  </p>
                  {threshold.guaranteed !== undefined && (
                    <p className="text-ink-600 text-sm">
                      Avec le majorant du cours, on le garantit dès n ={' '}
                      {formatCount(threshold.guaranteed, threshold.guaranteed > 1e7)} : c’est ce
                      rang qu’on justifie en copie.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-ink-700 text-sm font-semibold">Seuil A</span>
                    <Segmented
                      ariaLabel="Seuil A"
                      value={aValue}
                      options={A_OPTIONS}
                      onChange={setAValue}
                    />
                  </div>
                  <p className="text-ink-900 text-sm">
                    {threshold.exact
                      ? `Sₙ > ${aValue} dès n = ${formatCount(threshold.n)}.`
                      : `Sₙ > ${aValue} : il faut n ≈ ${formatCount(threshold.n)} termes (ordre de grandeur, tiré d’un développement de Sₙ).`}
                  </p>
                </>
              )}
            </section>
          )}

          <details className="border-ink-100 group rounded-2xl border">
            <summary className="text-ink-800 flex min-h-11 cursor-pointer list-none items-center px-3 text-sm font-semibold">
              Programme Python de Sₙ
            </summary>
            <pre
              className="text-ink-800 bg-ink-50 overflow-x-auto rounded-b-2xl px-3 py-2 text-[13px] leading-snug"
              aria-label="Programme Python des sommes partielles"
            >
              {pythonProgram(kind, p)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}

/** « S = π²/6 ≈ 1,644934 » ; pas de « ≈ » pour une somme décimale exacte (2 ; 0,5). */
function sumTex(sum: { tex: string; plain: string; value: number }): string {
  if (/^−?\d+(,\d+)?$/.test(sum.plain)) return sum.tex;
  const approx = formatNumber(sum.value, 6).replace(',', '{,}').replace('−', '-');
  return `${sum.tex} \\approx ${approx}`;
}

function labelOf<T extends string>(options: ReadonlyArray<{ value: T; label: string }>, value: T) {
  return options.find((o) => o.value === value)?.label ?? value;
}

function Legend({
  color,
  dashed = false,
  children,
}: {
  color: string;
  dashed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-1.5">
      <svg width="18" height="8" aria-hidden>
        <line
          x1={1}
          y1={4}
          x2={17}
          y2={4}
          stroke={color}
          strokeWidth={dashed ? 1.5 : 3}
          strokeDasharray={dashed ? '4 3' : undefined}
          strokeLinecap="round"
        />
      </svg>
      {children}
    </li>
  );
}

/** Grille, axes, graduations ; en échelle log, une graduation par puissance de 10. */
function Axes({ frame, log }: { frame: Frame; log: boolean }) {
  const { vp, size, tx, ty } = frame;
  const ys = ticks(vp.yMin, vp.yMax, niceStep(vp.yMax - vp.yMin, 6));
  const xs = log
    ? ticks(0, Math.floor(vp.xMax), 1)
    : ticks(Math.max(0, vp.xMin), vp.xMax, niceStep(vp.xMax - Math.max(0, vp.xMin), 5));
  const axisY = vp.yMin <= 0 && vp.yMax >= 0 ? ty(0) : size;
  const text = (v: number) => formatTick(v).replace('.', ',').replace('-', '−');
  const xLabel = (v: number) =>
    log ? (v <= 2 ? String(10 ** v) : `10${superscript(v)}`) : text(v);
  return (
    <g aria-hidden>
      {xs.map((x) => (
        <line key={`gx${x}`} x1={tx(x)} y1={0} x2={tx(x)} y2={size} stroke={GRID} strokeWidth={1} />
      ))}
      {ys.map((y) => (
        <line key={`gy${y}`} x1={0} y1={ty(y)} x2={size} y2={ty(y)} stroke={GRID} strokeWidth={1} />
      ))}
      <line x1={0} y1={axisY} x2={size} y2={axisY} stroke={AXIS} strokeWidth={1.25} />
      {xs
        .filter((x) => tx(x) > 10 && tx(x) < size - 10)
        .map((x) => (
          <text
            key={`tx${x}`}
            x={tx(x)}
            y={Math.min(size - 4, axisY + 14)}
            textAnchor="middle"
            fontSize={12}
            fill="var(--color-ink-500)"
          >
            {xLabel(x)}
          </text>
        ))}
      {ys
        .filter((y) => y !== 0 && ty(y) > 14 && ty(y) < size - 4)
        .map((y) => (
          <text key={`ty${y}`} x={4} y={ty(y) - 3} fontSize={12} fill="var(--color-ink-500)">
            {text(y)}
          </text>
        ))}
    </g>
  );
}

function sumsPath(frame: Frame, sums: readonly number[], first: number, X: (k: number) => number) {
  let d = '';
  sums.forEach((s, i) => {
    if (!Number.isFinite(s) || Math.abs(s) > 1e6) return;
    const k = i + first;
    if (k === 0 && X(1) !== 1) return; // pas de log 0
    d += `${d ? 'L' : 'M'}${frame.tx(X(k)).toFixed(1)},${frame.ty(s).toFixed(1)}`;
  });
  return d;
}

function curvePath(
  frame: Frame,
  first: number,
  n: number,
  f: (k: number) => number,
  X: (k: number) => number,
) {
  const pts = 120;
  let d = '';
  for (let i = 0; i <= pts; i++) {
    const k = Math.max(1, first) * (n / Math.max(1, first)) ** (i / pts);
    const y = f(k);
    if (!Number.isFinite(y)) continue;
    d += `${d ? 'L' : 'M'}${frame.tx(X(k)).toFixed(1)},${frame.ty(y).toFixed(1)}`;
  }
  return d;
}

/** Points (jusqu'à 60 termes) ou ligne brisée ; deux couleurs si Sₙ alterne. */
function SumsLayer({
  frame,
  sums,
  first,
  X,
  alternate,
}: {
  frame: Frame;
  sums: readonly number[];
  first: number;
  X: (k: number) => number;
  alternate: boolean;
}) {
  if (sums.length > 60) {
    return (
      <path
        d={sumsPath(frame, sums, first, X)}
        fill="none"
        stroke={POINT}
        strokeWidth={alternate ? 1.25 : 2.5}
        strokeLinejoin="round"
        data-testid="ps-line"
      />
    );
  }
  return (
    <g data-testid="ps-points">
      {sums.map((s, i) => {
        const k = i + first;
        if (!Number.isFinite(s) || Math.abs(s) > 1e6) return null;
        return (
          <circle
            key={k}
            cx={frame.tx(X(k))}
            cy={frame.ty(s)}
            r={sums.length > 30 ? 3 : 4.5}
            fill={alternate && k % 2 === 0 ? EVEN : POINT}
          />
        );
      })}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Défi « converge ou diverge ? »

interface QuizState {
  items: QuizItem[];
  round: number;
  answer: boolean | null;
  score: number;
}

function startQuiz(rng: () => number): QuizState {
  return { items: pickQuiz(rng), round: 0, answer: null, score: 0 };
}

function QuizPanel({
  state,
  onChange,
  onRestart,
}: {
  state: QuizState;
  onChange: (s: QuizState) => void;
  onRestart: () => void;
}) {
  const done = state.round >= state.items.length;
  if (done) {
    return (
      <section className="bg-ink-50 space-y-3 rounded-2xl p-4" data-testid="ps-quiz">
        <p className="text-ink-900 text-lg font-bold">
          {state.score} / {state.items.length} bonnes réponses
        </p>
        <button type="button" className={PRIMARY_BUTTON} onClick={onRestart}>
          Rejouer
        </button>
      </section>
    );
  }
  const item = state.items[state.round]!;
  const answered = state.answer !== null;
  const right = answered && state.answer === item.converges;
  const answer = (converges: boolean) => {
    if (answered) return;
    onChange({
      ...state,
      answer: converges,
      score: state.score + (converges === item.converges ? 1 : 0),
    });
  };
  return (
    <section className="border-ink-100 space-y-3 rounded-2xl border p-4" data-testid="ps-quiz">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        Série {state.round + 1}/{QUIZ_ROUNDS} · {state.score} point{state.score > 1 ? 's' : ''}
      </p>
      <MathText className="text-ink-900 block text-center text-xl">{`$${item.tex}$`}</MathText>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, answered && item.converges && 'border-success-500')}
          disabled={answered}
          onClick={() => answer(true)}
        >
          Converge
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, answered && !item.converges && 'border-success-500')}
          disabled={answered}
          onClick={() => answer(false)}
        >
          Diverge
        </button>
      </div>
      {answered && (
        <>
          <Note tone={right ? 'success' : 'error'}>
            {right ? 'Bien vu. ' : `Non : elle ${item.converges ? 'converge' : 'diverge'}. `}
            {item.why}
          </Note>
          <button
            type="button"
            className={PRIMARY_BUTTON}
            onClick={() => onChange({ ...state, round: state.round + 1, answer: null })}
          >
            {state.round + 1 < state.items.length ? 'Série suivante' : 'Voir mon score'}
          </button>
        </>
      )}
    </section>
  );
}
